import { useEffect, useState } from "react";
import type { NotificationsResponseDto, PlaybackUrlResponseDto, ViewerVideosResponseDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { fetchTeacherNotifications } from "../../shared/api/notification";
import { createTeacherPlaybackUrl, fetchTeacherVideos } from "../../shared/api/video";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const teacherLinks = [
  { to: "/t/:tenantSlug/teacher", label: "講師ホーム" },
  { to: "/t/:tenantSlug/teacher/admin", label: "管理者" }
];

export function TeacherHomePage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [notifications, setNotifications] = useState<NotificationsResponseDto | null>(null);
  const [videos, setVideos] = useState<ViewerVideosResponseDto | null>(null);
  const [playbacks, setPlaybacks] = useState<Record<string, PlaybackUrlResponseDto>>({});
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [playbackVideoId, setPlaybackVideoId] = useState<string | null>(null);
  const hasTeacherRole = Boolean(session?.member?.roles.includes("teacher"));
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasTeacherRole) {
      return;
    }

    let cancelled = false;

    async function loadVideos() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextVideos, nextNotifications] = await Promise.all([
          fetchTeacherVideos(tenantSlug),
          fetchTeacherNotifications(tenantSlug)
        ]);

        if (cancelled) {
          return;
        }

        setVideos(nextVideos);
        setNotifications(nextNotifications);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "teacher videos の取得に失敗しました");
      }
    }

    void loadVideos();

    return () => {
      cancelled = true;
    };
  }, [hasTeacherRole, status, tenantSlug]);

  async function handlePlayback(videoId: string) {
    setPlaybackVideoId(videoId);
    setLoadError(null);

    try {
      const nextPlayback = await createTeacherPlaybackUrl(tenantSlug, videoId);
      setPlaybacks((current) => ({
        ...current,
        [videoId]: nextPlayback
      }));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "playback url の取得に失敗しました");
    } finally {
      setPlaybackVideoId(null);
    }
  }

  return (
    <PageScaffold
      title="Teacher Home"
      description="講師向けホームの最小版です。担当 lesson / occurrence に紐づく動画だけ表示します。"
      links={teacherLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            teacher 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock teacher login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasTeacherRole && hasAdminRole ? (
          <p>admin session です。動画の upload / publish 管理は <Link to={`/t/${tenantSlug}/teacher/admin/videos`}>admin videos</Link> を利用してください。</p>
        ) : null}
        {status === "authenticated" && !hasTeacherRole && !hasAdminRole ? <p>この画面は teacher role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>teacher videos を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {hasTeacherRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>通知</h2>
              {notifications?.items.length === 0 ? <p style={{ margin: 0 }}>担当に関する通知はありません。</p> : null}
              {notifications?.items.map((item) => (
                <article
                  key={item.itemId}
                  style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 8 }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <strong>{item.title}</strong>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>{item.priority}</span>
                  </div>
                  <div style={{ fontSize: 14 }}>{item.body}</div>
                  {item.targetType && item.targetId ? (
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      {item.targetType} / {item.targetId}
                    </span>
                  ) : null}
                </article>
              ))}
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>担当動画</h2>
              {videos?.items.length === 0 ? <p style={{ margin: 0 }}>担当動画はありません。</p> : null}
              {videos?.items.map((video) => {
                const playback = playbacks[video.videoId];

                return (
                  <article
                    key={video.videoId}
                    style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 8 }}
                  >
                    <strong>{video.title ?? "タイトル未設定"}</strong>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {video.videoPurpose} / {video.targetType} / {video.targetLabel}
                    </div>
                    {video.description ? <div style={{ fontSize: 14 }}>{video.description}</div> : null}
                    <button
                      type="button"
                      onClick={() => void handlePlayback(video.videoId)}
                      disabled={playbackVideoId === video.videoId}
                    >
                      {playbackVideoId === video.videoId ? "取得中..." : "Playback URL"}
                    </button>
                    {playback ? (
                      <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
                        <a href={playback.playbackUrl} target="_blank" rel="noreferrer">
                          {playback.playbackUrl}
                        </a>
                        <span>expiresAt: {playback.expiresAt}</span>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </section>
          </>
        ) : null}

        <p style={{ margin: 0 }}>ここに今日の担当、近日の予定、欠勤申請導線を配置します。</p>
      </div>
    </PageScaffold>
  );
}

export { TeacherAbsenceRequestPage } from "./TeacherAbsenceRequestPage";
export { TeacherOccurrenceDetailPage } from "./TeacherOccurrenceDetailPage";
